/**
 * Binary tail client for hermes mailbox channels (rpc-inbox / rpc-sent).
 *
 * Reads GET /api/channel_tail/{key}/{channel}[/{startPos}] which streams the raw
 * NATS records as length-prefixed BINARY frames and stays open, pushing new
 * records as they are published. The records are protobuf (Message on rpc-inbox,
 * SendReceipt on rpc-sent), forwarded verbatim by the server — we decode them here
 * with the existing generated decoders. No JSON / base64 round-trip.
 *
 * Wire format, repeated per record, big-endian:
 *   uint64 streamSequence
 *   uint64 createdMillis    (NATS store timestamp, unix millis)
 *   uint32 payloadLength
 *   payloadLength bytes of raw protobuf
 */

import { Message, SendReceipt } from "./proto/wsmessages";

const FRAME_HEADER_BYTES = 20; // uint64 seq + uint64 createdMillis + uint32 len

export interface ChannelTailRecord {
  /** JetStream sequence — reconnect with startPos = seq + 1 to resume. */
  sequence: number;
  /** Server store time (unix millis); the raw record carries no serverEnvelope. */
  created: number;
  /** The raw protobuf bytes of the record. */
  bytes: Uint8Array;
  /** Decoded record: a SendReceipt for rpc-sent, a Message for other channels. */
  decoded: Message | SendReceipt;
}

export interface ChannelTailOptions {
  rootUrl: string;
  /**
   * Admin key OR reader key — the server resolves either.
   *
   * Prefer `processUid` where you have one. A mailbox key is a CREDENTIAL, and a UI
   * has no business holding one to read a log: /api/processrun_tail lets the caller
   * name a process run it is entitled to see and lets the SERVER resolve the mailbox.
   * This stays for callers that genuinely only have a key.
   */
  key?: string;
  /**
   * Tail by PROCESS RUN instead of by mailbox key (/api/processrun_tail). The server
   * authorizes the caller against the run — the same gate as reading its logs — and
   * resolves the mailbox itself, so the reader key never reaches the client.
   * Mutually exclusive with `key`; this one wins if both are set.
   */
  processUid?: string;
  /** Channel name, e.g. "rpc-inbox" or "rpc-sent". */
  channel: string;
  /** Where to start: "first" (replay history then follow), "tail"/"new" (only
   * new records), or a sequence number. Defaults to "first" server-side. */
  startPos?: string | number;
  authToken?: string;
  /** Abort the stream by aborting this signal. */
  signal?: AbortSignal;
  /** Called for each decoded record as it arrives. */
  onRecord: (record: ChannelTailRecord) => void;
  /** Called on a fatal stream error (network, decode). */
  onError?: (err: unknown) => void;
}

function isRpcSent(channel: string): boolean {
  return channel === "rpc-sent";
}

/**
 * Open a channel tail. Resolves when the stream ends (server closed or the signal
 * was aborted); rejects on a fatal error if no onError handler is provided.
 *
 * A frame can be split across network chunks, so we accumulate bytes and only
 * emit a record once the full header + payload are available.
 */
export async function tailChannel(opts: ChannelTailOptions): Promise<void> {
  const { rootUrl, key, processUid, channel, startPos, authToken, signal, onRecord, onError } = opts;

  if (!processUid && !key) {
    throw new Error("tailChannel needs a processUid or a mailbox key");
  }

  const decode = isRpcSent(channel)
    ? (b: Uint8Array) => SendReceipt.decode(b)
    : (b: Uint8Array) => Message.decode(b);

  // Same wire format either way; only the addressing differs. processrun_tail is
  // preferred — it keeps the mailbox's reader key on the server.
  let url = processUid
    ? `${rootUrl}/api/processrun_tail/${encodeURIComponent(processUid)}/${encodeURIComponent(channel)}`
    : `${rootUrl}/api/channel_tail/${encodeURIComponent(key!)}/${encodeURIComponent(channel)}`;
  if (startPos !== undefined) {
    url += `/${encodeURIComponent(String(startPos))}`;
  }

  const headers: Record<string, string> = {};
  if (authToken) {
    // These endpoints authenticate via X-Auth-Token (else the Auth-Token cookie);
    // the legacy X-SESS header is not read here. Matches the /api/subscribe client.
    headers["X-Auth-Token"] = authToken;
  }

  try {
    const response = await fetch(url, { method: "GET", headers, signal });
    if (!response.ok || !response.body) {
      throw new Error(`channel_tail failed with status ${response.status}`);
    }

    const reader = response.body.getReader();

    // Rolling buffer of bytes not yet consumed into a complete frame.
    let buffer = new Uint8Array(0);

    const append = (chunk: Uint8Array) => {
      const next = new Uint8Array(buffer.length + chunk.length);
      next.set(buffer, 0);
      next.set(chunk, buffer.length);
      buffer = next;
    };

    // Emit every complete frame currently in the buffer.
    const drainFrames = () => {
      // Loop while at least a header is buffered.
      while (buffer.length >= FRAME_HEADER_BYTES) {
        const view = new DataView(buffer.buffer, buffer.byteOffset, buffer.byteLength);
        // uint64 fields — JS numbers are safe to ~2^53; these won't exceed that.
        const seq = Number(view.getBigUint64(0, false));
        const created = Number(view.getBigUint64(8, false));
        const len = view.getUint32(16, false);
        const total = FRAME_HEADER_BYTES + len;
        if (buffer.length < total) {
          break; // payload not fully arrived yet
        }
        const bytes = buffer.slice(FRAME_HEADER_BYTES, total);
        buffer = buffer.slice(total);
        onRecord({ sequence: seq, created, bytes, decoded: decode(bytes) });
      }
    };

    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      if (value && value.length) {
        append(value);
        drainFrames();
      }
    }
  } catch (err) {
    if (signal?.aborted) {
      return; // intentional stop, not an error
    }
    if (onError) {
      onError(err);
      return;
    }
    throw err;
  }
}
